import { resolveErrorMessage } from '../../utils/errorMessage';

const getBindingSummaryPatch = ({ accountId, account = {}, bindings = [], primaryAgentId }) => {
  const normalizedPrimary = String(primaryAgentId || account.primary_agent_id || account.agent_id || '').trim() || undefined;
  const enabledBindings = (bindings || []).filter((row) => row?.enabled !== false);
  const backupAgentsCount = Math.max(0, enabledBindings.length - (normalizedPrimary ? 1 : 0));
  return {
    ...(account || {}),
    account_id: accountId,
    primary_agent_id: normalizedPrimary,
    agent_id: normalizedPrimary,
    backup_agents_count: backupAgentsCount,
  };
};

export const createLoadAgentsHandler = ({
  setAgentsLoading,
  listAgents,
  setAgentList,
  message,
}) => async ({ silent = false } = {}) => {
  setAgentsLoading(true);
  try {
    const response = await listAgents({
      page: 1,
      page_size: 100,
      include_accounts: true,
      online_window_sec: 120,
    });
    if (response.status === 'success') {
      setAgentList(Array.isArray(response.items) ? response.items : []);
    } else if (!silent) {
      message.error(resolveErrorMessage(response.error_message, '加载 Agent 列表失败'));
    }
  } catch (error) {
    console.error('加载 Agent 列表失败:', error);
    if (!silent) message.error('加载 Agent 列表失败，请稍后重试');
  } finally {
    setAgentsLoading(false);
  }
};

export const createLoadAgentBindingsHandler = ({
  listAgentBindings,
  setAgentBindingsLoading,
  setAgentBindingItems,
  setAgentBindingPrimaryAgentId,
  agentBindTarget,
  operatorId = '',
  message,
}) => async ({ silent = false, accountId: accountIdRaw } = {}) => {
  const accountId = String(accountIdRaw || agentBindTarget?.account_id || '').trim();
  if (!accountId) return null;
  setAgentBindingsLoading(true);
  try {
    const response = await listAgentBindings({
      account_id: accountId,
      include_offline: true,
      operator_id: operatorId || undefined,
    });
    if (response?.status === 'success') {
      setAgentBindingItems(Array.isArray(response.items) ? response.items : []);
      setAgentBindingPrimaryAgentId(String(response.primary_agent_id || '').trim() || undefined);
      return response;
    }
    if (!silent) {
      message.error(resolveErrorMessage(response?.error_message, '加载账号绑定关系失败'));
    }
    return response;
  } catch (error) {
    console.error('加载账号绑定关系失败:', error);
    if (!silent) message.error(error?.message || '加载账号绑定关系失败');
    return null;
  } finally {
    setAgentBindingsLoading(false);
  }
};

export const createCloseAgentBindModalHandler = ({
  setAgentBindVisible,
  setAgentBindTarget,
  setAgentBindingItems,
  setAgentBindingPrimaryAgentId,
  agentBindForm,
}) => () => {
  setAgentBindVisible(false);
  setAgentBindTarget(null);
  setAgentBindingItems([]);
  setAgentBindingPrimaryAgentId(undefined);
  agentBindForm.resetFields();
};

export const createOpenAgentBindModalHandler = ({
  setAgentBindTarget,
  agentBindForm,
  setAgentBindVisible,
  loadAgents,
  loadAgentBindings,
  message,
}) => (record) => {
  if (!record?.account_id) {
    message.error('缺少账号 ID，无法维护 Agent 绑定');
    return;
  }
  setAgentBindTarget(record);
  agentBindForm.setFieldsValue({
    role: 'primary',
    priority: 1,
    enabled: true,
  });
  setAgentBindVisible(true);
  loadAgents({ silent: false });
  loadAgentBindings({ silent: false, accountId: record.account_id });
};

export const createSyncAccountLocalStateHandler = ({
  normalizeOptionalString,
  setDataSource,
  recentlyUpdatedAccountsRef,
}) => (account) => {
  const accountId = normalizeOptionalString(account?.account_id);
  if (!accountId) return;
  const patch = { ...account, account_id: accountId };
  setDataSource((prev) => (prev || []).map((item) => (
    item?.account_id === accountId ? { ...item, ...patch } : item
  )));
  recentlyUpdatedAccountsRef.current.set(accountId, { ts: Date.now(), patch });
};

export const createCreateAgentBindingHandler = ({
  message,
  upsertAgentBinding,
  syncAccountLocalState,
  drawerMode,
  drawerAccount,
  normalizeOptionalString,
  setDrawerAccount,
  detailForm,
  reloadCurrentAccountList,
  loadAgents,
  loadAgentBindings,
  setAgentBindSubmitting,
  agentBindForm,
  agentBindTarget,
  operatorId = '',
}) => async () => {
  const accountId = normalizeOptionalString(agentBindTarget?.account_id);
  if (!accountId) {
    message.error('缺少账号 ID，无法创建绑定');
    return false;
  }
  try {
    const values = await agentBindForm.validateFields();
    const agentId = normalizeOptionalString(values.agent_id);
    if (!agentId) {
      message.warning('请选择一个 Agent');
      return false;
    }
    setAgentBindSubmitting(true);
    message.loading({ content: '正在保存绑定...', key: 'bindAgent' });
    const role = values.role || 'primary';
    const priority = Number(values.priority || 1);
    const response = await upsertAgentBinding({
      account_id: accountId,
      agent_id: agentId,
      operator_id: operatorId || undefined,
      role,
      priority,
      enabled: values.enabled !== false,
      set_as_primary: role === 'primary',
    });
    if (response?.status === 'success') {
      const patch = getBindingSummaryPatch({
        accountId,
        account: response.account || {},
        bindings: response.items || [],
        primaryAgentId: response.primary_agent_id,
      });
      syncAccountLocalState(patch);
      if (drawerMode === 'edit' && normalizeOptionalString(drawerAccount?.account_id) === accountId) {
        setDrawerAccount((prev) => ({ ...(prev || {}), ...patch }));
        detailForm.setFieldsValue({
          platform_config: patch.platform_config ? JSON.stringify(patch.platform_config, null, 2) : '',
        });
      }
      message.success({ content: '绑定已保存', key: 'bindAgent' });
      agentBindForm.setFieldsValue({ agent_id: undefined, role: 'primary', priority: 1, enabled: true });
      await Promise.all([
        reloadCurrentAccountList(),
        loadAgents({ silent: true }),
        loadAgentBindings({ silent: true, accountId }),
      ]);
      return true;
    }
    message.error({ content: resolveErrorMessage(response?.error_message, '保存绑定失败'), key: 'bindAgent' });
    return false;
  } catch (error) {
    if (error?.errorFields) return false;
    console.error('保存绑定失败:', error);
    message.error({ content: error?.message || '保存绑定失败，请稍后重试', key: 'bindAgent' });
    return false;
  } finally {
    setAgentBindSubmitting(false);
  }
};

export const createUpdateAgentBindingHandler = ({
  message,
  upsertAgentBinding,
  syncAccountLocalState,
  drawerMode,
  drawerAccount,
  normalizeOptionalString,
  setDrawerAccount,
  detailForm,
  reloadCurrentAccountList,
  loadAgentBindings,
  setAgentBindSubmitting,
  agentBindTarget,
  operatorId = '',
}) => async (row, patch = {}) => {
  const accountId = normalizeOptionalString(agentBindTarget?.account_id);
  const agentId = normalizeOptionalString(row?.agent_id);
  if (!accountId || !agentId) return false;
  setAgentBindSubmitting(true);
  try {
    const response = await upsertAgentBinding({
      account_id: accountId,
      agent_id: agentId,
      operator_id: operatorId || undefined,
      role: patch.role || row?.role || 'secondary',
      priority: Number(patch.priority ?? row?.priority ?? 100),
      enabled: patch.enabled ?? (row?.enabled !== false),
      set_as_primary: patch.set_as_primary === true,
    });
    if (response?.status === 'success') {
      const accountPatch = getBindingSummaryPatch({
        accountId,
        account: response.account || {},
        bindings: response.items || [],
        primaryAgentId: response.primary_agent_id,
      });
      syncAccountLocalState(accountPatch);
      if (drawerMode === 'edit' && normalizeOptionalString(drawerAccount?.account_id) === accountId) {
        setDrawerAccount((prev) => ({ ...(prev || {}), ...accountPatch }));
        detailForm.setFieldsValue({
          platform_config: accountPatch.platform_config ? JSON.stringify(accountPatch.platform_config, null, 2) : '',
        });
      }
      await Promise.all([
        reloadCurrentAccountList(),
        loadAgentBindings({ silent: true, accountId }),
      ]);
      return true;
    }
    message.error(resolveErrorMessage(response?.error_message, '更新绑定失败'));
    return false;
  } catch (error) {
    console.error('更新绑定失败:', error);
    message.error(error?.message || '更新绑定失败');
    return false;
  } finally {
    setAgentBindSubmitting(false);
  }
};

export const createDeleteAgentBindingHandler = ({
  message,
  deleteAgentBinding,
  syncAccountLocalState,
  drawerMode,
  drawerAccount,
  normalizeOptionalString,
  setDrawerAccount,
  detailForm,
  reloadCurrentAccountList,
  loadAgentBindings,
  setAgentBindSubmitting,
  agentBindTarget,
  operatorId = '',
}) => async (row) => {
  const accountId = normalizeOptionalString(row?.account_id || agentBindTarget?.account_id);
  const agentId = normalizeOptionalString(row?.agent_id);
  if (!accountId || !agentId) return false;
  setAgentBindSubmitting(true);
  try {
    const response = await deleteAgentBinding({
      account_id: accountId,
      agent_id: agentId,
      operator_id: operatorId || undefined,
    });
    if (response?.status === 'success') {
      const patch = getBindingSummaryPatch({
        accountId,
        bindings: response.items || [],
        primaryAgentId: response.primary_agent_id,
      });
      syncAccountLocalState(patch);
      if (drawerMode === 'edit' && normalizeOptionalString(drawerAccount?.account_id) === accountId) {
        setDrawerAccount((prev) => ({ ...(prev || {}), ...patch }));
        detailForm.setFieldsValue({
          platform_config: patch.platform_config ? JSON.stringify(patch.platform_config, null, 2) : '',
        });
      }
      message.success('绑定已删除');
      await Promise.all([
        reloadCurrentAccountList(),
        loadAgentBindings({ silent: true, accountId }),
      ]);
      return true;
    }
    message.error(resolveErrorMessage(response?.error_message, '删除绑定失败'));
    return false;
  } catch (error) {
    console.error('删除绑定失败:', error);
    message.error(error?.message || '删除绑定失败');
    return false;
  } finally {
    setAgentBindSubmitting(false);
  }
};

export const createSetPrimaryAgentBindingHandler = ({
  updateAgentBinding,
}) => async (row) => updateAgentBinding(row, { role: 'primary', set_as_primary: true });

export const createHandleAgentUnbindHandler = ({
  Modal,
  normalizeOptionalString,
  message,
  agentBindTarget,
  bindingPrimaryAgentId,
  deleteAgentBinding,
  operatorId = '',
}) => (record) => {
  const accountId = normalizeOptionalString(record?.account_id || agentBindTarget?.account_id);
  const agentId = normalizeOptionalString(
    record?.primary_agent_id || bindingPrimaryAgentId || record?.agent_id || agentBindTarget?.primary_agent_id || agentBindTarget?.agent_id,
  );
  if (!accountId || !agentId) {
    message.warning('当前账号没有可解绑的主绑定');
    return;
  }
  Modal.confirm({
    title: '确认解绑主绑定 Agent',
    content: `确定要删除主绑定 "${agentId}" 吗？`,
    okText: '解绑',
    okType: 'danger',
    cancelText: '取消',
    onOk: async () => {
      await deleteAgentBinding({
        account_id: accountId,
        agent_id: agentId,
        operator_id: operatorId || undefined,
      });
    },
  });
};

export const buildAgentSelectOptions = (agentList = []) => (
  (agentList || []).map((agent) => {
    const host = [agent.hostname, agent.device_id].filter(Boolean).join(' / ') || agent.agent_id;
    const onlineText = agent.is_online ? '在线' : '离线';
    const type = String(agent.agent_type || 'local').trim();
    const boundCount = Number.isFinite(Number(agent.bound_account_count)) ? Number(agent.bound_account_count) : 0;
    return {
      value: agent.agent_id,
      label: `${agent.is_online ? '🟢' : '⚪'} ${host} · ${type} · ${onlineText} · 已绑 ${boundCount} 个 · ${agent.agent_id}`,
    };
  })
);
